// [2026-09-04] ADDED — the revamped customer landing screen, built to the IncProctor Figma.
//
// WHAT THIS IS, IN ONE LINE: the Figma's "Dashboard" is the old Overview's account tiles and the
// old "Usage & analytics" screen on ONE page, in the design's order.
//
// NOTHING WAS DELETED TO BUILD IT. PlatformOverview.jsx and PlatformAnalytics.jsx are untouched on
// disk and both still render — PlatformApp routes 'analytics' at the second one and the account
// menu links to it. This file is a THIRD view that composes the same data into the design's layout.
// If the revamp is reverted, PlatformApp's 'overview' route goes back to PlatformOverview and
// nothing else has to move.
//
// ONE REQUEST STILL FEEDS EVERY PANEL. GET /me/analytics gathers its nine reads concurrently
// server-side (app/modules/me/route.py:343), so this page either has its data or is still loading;
// it never renders four populated charts beside five empty ones. The two extra calls here are
// /me/keys (for the key count tile) and the session already fetched by the shell — neither blocks
// the charts, which is why the tiles show "—" rather than 0 while they are in flight. A dashboard
// that prints 0 for a number it does not yet know is stating something false.

import { platform } from '../../api.js'
import { ms, num, pct, useAsync } from '../../ui.jsx'
import {
  // [2026-09-11] NOTE — TrafficChart is imported but no longer rendered here: its card was switched
  // off on 2026-09-11 (see the block near "── traffic ──" below). KEPT ON PURPOSE, so uncommenting
  // that block is the whole revert. It costs nothing in the bundle — charts.jsx is imported for the
  // Sessions chart either way, and an unused binding is dropped at build time.
  CandidateSessionsChart, Heatmap, HourOfDayChart, LatencyHistogram, LatencyTrend, TrafficChart,
} from '../../charts.jsx'
import { PfCard, PfEmpty, PfNotice, PfPill, PfSkeleton, PfTile } from '../../components/platform/ui.jsx'
import {
  BriefcaseIcon, CalendarIcon, CardIcon, CheckCircleIcon, MessageIcon, ReportIcon, TrendingUpIcon,
  UsersIcon, ZapIcon,
} from '../../components/platform/icons.jsx'
// [2026-09-06] ADDED — the per-service rollup, shared with the Credits and Analytics screens so all
// three agree on which endpoint belongs to which product.
import { rollUpByService, SERVICE_VIDEO, SERVICE_SDK } from '../../services.js'
// [2026-09-25] ADDED — the account app's address for THIS environment, for the Add Subuser quick
// action below. The same helper the header's "Add Users" and account-menu links use.
import { accountHref } from '../../components/platform/productLinks.js'

// ── the dashboard's chart palette ─────────────────────────────────────────────
//
// READ THE WARNING ON `withColors` IN charts.jsx BEFORE CHANGING A HEX HERE.
//
// The default palette in charts.jsx (blue #2a78d6 / orange #eb6834 / aqua #1baf7a) carries a
// MEASURED colour-blindness result — worst all-pairs ΔE 9.2 deutan, normal-vision 24.0, both
// against a white card. This set is different, because the Figma is drawn in the InCruiter brand
// colours, so that measurement does NOT carry over and is not being claimed here.
//
// What has been kept deliberately, so the substitution is defensible rather than decorative:
//   * The three traffic series stay in three clearly separated LIGHTNESS bands — teal #12a57e is
//     mid, navy #133f7d is dark, red #d9453f is mid-dark but strongly differing in hue from the
//     teal. Deutan/protan confusion is worst between red and GREEN of similar lightness; the navy
//     between them is the separation that keeps the stack readable.
//   * Every chart using them still ships a legend or a table beside it, which is the relief the
//     original note requires for any series below 3:1 on white.
//   * The status colours are still reserved and are still never used as a series colour.
//
// ⚠ VERIFY-BEFORE-PROD: this set has NOT been through the contrast/CVD validator that produced the
// numbers above. That check should be run before this ships to customers. It is flagged rather than
// silently assumed.
const BRAND_CHARTS = {
  series1: '#12a57e',   // teal-green — "success", and the sessions area
  series2: '#12b6bc',   // the logo's teal — the p95 series beside the average
  warning: '#133f7d',   // the logo's navy — "client errors (4xx)" in the Figma's legend
  critical: '#d9453f',  // red — "server errors (5xx)"
  axis:    '#c9d2de',
  grid:    '#eef1f6',
  muted:   '#7c8aa0',
  // A navy sequential ramp for the heatmap and the latency buckets, ending at the brand navy.
  seq: ['#f4f7fb', '#dce8f7', '#bcd3ee', '#94b7e2', '#6b98d3', '#4676bd', '#2a599e', '#133f7d'],
}

// The navy is what the Figma paints the single-series bars in (hour-of-day, sessions).
const NAVY = '#133f7d'

/** The range control the Figma puts on the Traffic card.
 *
 *  LABELLED IN DAYS, NOT "This Month". The mock says "This Month", but the API window is a ROLLING
 *  count of days (`GET /me/analytics?days=`), not a calendar month — on the 3rd of September "This
 *  Month" would mean three days of data while the chart drew thirty. A control whose label
 *  disagrees with what it does is worse than a plainer one that is true. */
function RangeSelect({ value, onChange }) {
  return (
    <select className="pf-select" value={value} aria-label="Time range"
            onChange={(event) => onChange(Number(event.target.value))}>
      <option value={7}>Last 7 days</option>
      <option value={30}>Last 30 days</option>
      <option value={90}>Last 90 days</option>
    </select>
  )
}

export default function PlatformDashboard({ session, days = 30, setDays, setView, onError,
                                            onDrillEndpoint, onCreateKey }) {
  const { data, error, loading } = useAsync(() => platform.analytics(days), [days])

  // [2026-09-06] ADDED — the two billed surfaces, each rolled up from the same /me/analytics
  // payload. No extra request: `endpoints` is already fetched above and `credits` already carries
  // the ledger's per-surface counters.
  //
  // THESE TWO PARTITION THE TRAFFIC — every endpoint belongs to exactly one of them (services.js),
  // so the tiles below never count one call twice and the pair sums to the account's total. That is
  // the whole reason the all-traffic totals were removed: a total plus one of its own subsets is
  // the same number printed twice.
  //
  // `null` while in flight, so the tiles render "—" rather than a confident 0.
  const byService = loading ? [] : rollUpByService(data?.endpoints)
  const video = loading ? null : byService.find((row) => row.key === SERVICE_VIDEO)
  const videoRate = video?.error_rate
  const videoFailed = video ? video.client_error + video.server_error : 0
  const sdk = loading ? null : byService.find((row) => row.key === SERVICE_SDK)
  const sdkRate = sdk?.error_rate
  const sdkFailed = sdk ? sdk.client_error + sdk.server_error : 0
  const { data: keys } = useAsync(() => platform.keys(), [])

  if (error) { onError?.(error); return <PfNotice kind="error">{error.message}</PfNotice> }

  const credits = session?.credits || {}
  // [2026-09-06] ADDED — the LEDGER's own per-surface counter for the analysis API (SURFACE_API in
  // app/core/credits.py). `?? null` and not `|| 0`: an account whose summary has not loaded gets
  // "—", while one that has genuinely spent nothing gets a real 0.
  //
  // ⚠️ THIS IS A COUNT OF CHARGES, NOT A SUM OF CREDITS. app/core/stores.py:407 does
  // `balance_inc[used] = 1` — one increment per debit, whatever the debit was worth. The two
  // numbers coincide today ONLY because video analysis is priced at exactly one credit
  // (GATEWAY_CREDIT_PRICE_VIDEO_ANALYZE_MILLI defaults to 1000 milli = 1.00), so "charges" and
  // "credits" are the same integer. THE MOMENT THAT PRICE CHANGES this tile would silently
  // under- or over-state spend. It is rendered with no decimals for that reason — presenting an
  // integer count as "6.00 credits" would assert a precision it does not have — and the fix, if
  // the price ever moves off 1.0, is a real per-surface credit total in the backend, NOT a
  // multiplication here.
  const videoCredits = credits.api_used ?? null
  // The SDK's own ledger counter (SURFACE_SDK). Absent entirely on an account that has never run a
  // candidate session, which `?? null` renders as "—" rather than asserting a zero.
  const sdkCredits = credits.sdk_used ?? null
  const t = data?.totals || {}
  const st = data?.session_totals || {}
  const lat = data?.latency || {}
  const activeKeys = keys?.active

  const events = data?.event_breakdown || { by_type: {} }
  const byType = Object.entries(events.by_type || {}).sort((a, b) => b[1] - a[1])
  const eventMax = byType.length ? byType[0][1] : 0

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {/* [2026-09-11] CHANGED — the low-balance banner is OFF. Asked for by Rujal: "remove it
          totally from the client front end."
          OLD: {credits.low_balance && (

          BOTH COPIES, NOT ONE. The near-identical sentence on Credits & Billing
          (PlatformCredits.jsx, same date) is switched off in the same change — they are the same
          message on the two screens a customer actually opens, so removing only one would have
          looked like nothing happened.

          `false &&` rather than deleting, per this repo's rule: the JSX stays live code, so
          `credits.low_balance` is still checked against the API shape and PfNotice stays imported
          and used. Deleting two words brings it back.

          The signal itself is NOT lost — the Credits Remaining tile still colours red on
          `low_balance` (see its `tone` prop below), which is the quiet version of the same warning
          and does not take a whole strip across the page to say it. */}
      {false && credits.low_balance && (
        <PfNotice kind="warn">
          Your credit balance is low. Credits are added by your InCruiter account manager — contact
          them to top up before your next batch of candidates.
        </PfNotice>
      )}

      {/* ── the eight headline tiles ───────────────────────────────────────
          Row one is the money, row two is the integration — the Figma's split, and the right one:
          the first question on opening this page is "can we still run exams", the second is "is the
          API healthy". */}
      <div className="pf-grid tiles">
        {/* [2026-09-25] CHANGED — `iconTone` added to these four tiles: the Figma's coloured icon
            chips (blue / teal / red / amber, in the Figma's order). Asked for by Rujal. The label,
            icon and figure of every tile are unchanged. OLD: the same four tiles without iconTone. */}
        <PfTile label="Total Credits" icon={CardIcon} iconTone="blue"
                value={num(credits.total_granted_credits ?? null, { decimals: 0 })} />
        <PfTile label="Credits Utilized" icon={TrendingUpIcon} iconTone="teal"
                value={num(credits.total_spent_credits ?? null, { decimals: 0 })} />
        {/* [2026-09-08] CHANGED — this shows AVAILABLE credits, not the ledger balance.
            OLD: value={num(credits.balance_credits ?? null, { decimals: 0 })}

            THE BUG, seen on dev: the tile read "Credits Remaining 1" while every attempt to start
            an interview was refused with INSUFFICIENT_CREDITS. Both numbers were correct and they
            were different numbers. `balance_credits` is what the ledger says the company owns;
            `available_credits` is what can actually be spent right now, because interview billing
            HOLDS a credit at mint until the video analysis completes. One credit, held, reads as
            balance 1 / available 0.

            A tile labelled "Remaining" that a customer cannot spend is worse than a wrong number:
            it is a number that actively contradicts what the product then does. The sub-line was
            already telling the truth ("1 held by runs in flight") and the big figure was arguing
            with it.

            `?? credits.balance_credits` so a browser running against a backend that does not yet
            send `available_credits` shows exactly what it showed before, rather than an em dash. */}
        <PfTile label="Credits Remaining" icon={ZapIcon} iconTone="red"
                value={num(credits.available_credits ?? credits.balance_credits ?? null,
                           { decimals: 0 })}
                tone={credits.low_balance ? 'var(--pf-bad)' : undefined}
                /* [2026-09-11] CHANGED — the held figure has moved OUT of this sub-line and into a
                   tile of its own, immediately to the right.
                   OLD: sub={credits.held_credits
                          ? `${num(credits.held_credits, { decimals: 0 })} held by runs in flight`
                          : undefined}

                   WHY IT MOVED RATHER THAN BEING COPIED. With an "In Progress" tile beside it the
                   old sub-line printed the SAME number twice, six inches apart, in two different
                   vocabularies. Two renderings of one figure is how a reader starts wondering
                   whether they are two figures.

                   WHY THE TILE AND NOT THE SUB-LINE WON. A sub-line is small grey type that says
                   "footnote"; this number is the entire reason Total − Utilized does not equal
                   Remaining, which is the question the screen was failing to answer. It is not a
                   footnote. */ />
        {/* [2026-09-11] ADDED — the held credits, as a tile of their own. Asked for by Rujal, in
            the same breath as switching the Sessions and Active API Keys tiles off — this is what
            takes their place.

            WHAT IT IS. A credit is HELD the moment a candidate session is minted and settled when
            the analysis finishes (hold-at-mint / settle-on-analysis, app/core/credits.py). So this
            is money that has left "Remaining" but has not yet been spent, and it is the missing
            term in the subtraction a customer does on this row:

                Total Credits − Credits Utilized − In Progress ≈ Credits Remaining

            ("≈", not "=", and that is deliberate — a manual ledger adjustment by an account manager
            lands in none of these four. The row gets the reader most of the way; the Credits &
            Billing ledger is where an exact reconciliation lives. Printing a fifth "Adjustments"
            tile on the landing page was tried on 2026-09-11 and reverted.)

            "returned if unused" IS THE POINT OF THE SUB-LINE. Without it, a number sitting between
            "Utilized" and "Remaining" reads as a second kind of spend — money gone. It is not: the
            settler releases the hold when an interview does not complete. That one clause is the
            difference between this tile reassuring somebody and alarming them.

            `?? null` -> "—" rather than 0, for the reason every other tile here does it: an account
            whose session has not loaded should not assert that nothing is running. */}
        {/* [2026-09-25] amber — the colour of the Figma's fourth tile (Sessions, which this tile
            replaced on 2026-09-11 and which used the same calendar icon). */}
        <PfTile label="In Progress" icon={CalendarIcon} iconTone="amber"
                value={num(credits.held_credits ?? null, { decimals: 0 })}
                sub="interviews running · returned if unused" />

        {/* [2026-09-11] CHANGED — the Sessions and Active API Keys tiles are off. Asked for by
            Rujal; the In Progress tile above is what replaces them.

            `false &&` and not a deletion, for the reason the 2026-09-07 note below already gives:
            the JSX stays live code, so `st`, `activeKeys` and CalendarIcon/UsersIcon are still
            referenced and a rename still fails the build. Deleting the two words brings both back.

            NOTE the /me/keys request above is now fetched only for a switched-off tile. It is left
            in place on purpose — it is one cheap call and removing it would make the revert two
            edits instead of one — but if these tiles are ever retired for good, that is the other
            half of the change. */}
        {false && (<>
        <PfTile label="Sessions" icon={CalendarIcon}
                value={loading ? '—' : num(st.sessions || 0)}
                note={st.live ? `${st.live} sessions live now` : undefined} />

        <PfTile label="Active API Keys" icon={UsersIcon}
                value={activeKeys === undefined ? '—' : num(activeKeys)} />
        </>)}

        {/* [2026-09-06] CHANGED — calls, error rate and latency are now shown PER SERVICE instead of
            as one all-traffic total plus a filtered copy of it.

            WHAT WAS WRONG. Row two summed every endpoint ("API Calls: 6") and row three showed the
            same figures filtered to proctoring ("Proctoring · Calls: 6"). The second was a SUBSET of
            the first, so on an account using one service the identical number was printed twice with
            two different labels — which reads as a double count and invites the reader to add them.
            An inclusion relationship the screen never states is a bad way to present two numbers.

            NOW THEY PARTITION. Each service's calls are counted once, in exactly one tile, and no
            tile contains another. Two services, two sets of figures, and the sum of the two IS the
            account's total — so nothing is hidden and nothing repeats. The all-traffic total is
            deliberately NOT shown alongside them for that reason; the "By Endpoint" table lower down
            already carries the full breakdown for anyone who wants to reconcile.

            OLD:
              <PfTile label={`API Calls (${days} days)`} … value={num(t.total_calls || 0)} />
              <PfTile label="Error Rate" … value={pct(t.error_rate || 0)} />
              <PfTile label="Avg. Latency" … value={ms(t.avg_latency_ms)} />
              (then a third row repeating all three, filtered to proctoring) */}
      {/* [2026-09-07] TAKEN OFF THE DASHBOARD — hidden, not deleted.
          Asked for: this screen had grown to fifteen panels, and a dashboard nobody scrolls to the
          end of answers nothing. What remains is what the page is opened to check — credits,
          sessions, keys, and what candidates actually did.

          `false &&` rather than a comment block, deliberately: the JSX stays live code, so it is
          still linted and still fails a build if a prop it uses is renamed. Commented-out markup
          rots silently and is discovered broken the day someone tries to restore it. Deleting the
          two words brings it back. Every figure below is still on Usage & Analytics, which is where
          someone goes when they are specifically investigating latency or endpoints. */}
      {false && (<>
        {/* LIFETIME, unlike the three beside it, and the sub-line says so. There is no dated
            per-surface breakdown in the credit summary, so this does NOT share their window —
            without that label a reader divides it by the call count and gets a meaningless
            credits-per-call. No decimals: it is an integer count of charges (app/core/stores.py
            increments by 1 per debit), and "4.00" would assert a precision it does not have. */}
        <PfTile label="Proctoring · Credits Utilized" icon={TrendingUpIcon}
                value={videoCredits == null ? '—' : num(videoCredits, { decimals: 0 })}
                sub="lifetime · 1 credit per interview" />
        <PfTile label={`Proctoring · Calls (${days} days)`} icon={MessageIcon}
                value={loading ? '—' : num(video?.total || 0)}
                sub={video && video.total > 0
                  ? `${num(video.success)} accepted by the engine`
                  : 'no analyses submitted'} />
        {/* null, not 0 — no calls means the rate is UNKNOWN and "—" says so. A literal 0.0% would
            claim a clean record for a window holding no evidence either way. The 4xx/5xx split is in
            the sub-line because they are two different conversations: a 4xx the customer can fix
            (out of credits, bad payload), a 5xx is ours. */}
        <PfTile label="Proctoring · Error Rate" icon={CheckCircleIcon}
                value={loading || videoRate == null ? '—' : pct(videoRate)}
                tone={videoRate != null && videoRate > 0.05 ? 'var(--pf-bad)' : undefined}
                sub={loading ? undefined
                  : videoRate == null ? 'no calls to measure'
                  : videoFailed === 0 ? 'no failures'
                  : `${num(video.client_error)} client · ${num(video.server_error)} server`} />
        {/* The measured time is the SUBMISSION handshake, not the analysis. The engine returns as
            soon as the job is queued and delivers the report by webhook minutes later
            (app/core/video_processing_proxy.py). */}
        <PfTile label="Proctoring · Avg. Latency" icon={BriefcaseIcon}
                value={loading || video?.avg_latency_ms == null ? '—' : ms(video.avg_latency_ms)}
                sub="time to queue, not to analyse" />

        {/* The OTHER billed surface, and the counterpart to the three above. Shown even at zero: a
            service missing from the screen reads as "not offered", when it means "not used yet" —
            and this deployment HAS the SDK enabled (GATEWAY_SDK_ENABLED), so a customer who has not
            integrated it should see that it exists and is idle rather than see nothing. */}
        <PfTile label="Browser SDK · Credits Utilized" icon={TrendingUpIcon}
                value={sdkCredits == null ? '—' : num(sdkCredits, { decimals: 0 })}
                sub="lifetime · per candidate session" />
        <PfTile label={`Browser SDK · Calls (${days} days)`} icon={MessageIcon}
                value={loading ? '—' : num(sdk?.total || 0)}
                sub={sdk && sdk.total > 0
                  ? `${num(sdk.success)} accepted`
                  : 'no candidate sessions yet'} />
        <PfTile label="Browser SDK · Error Rate" icon={CheckCircleIcon}
                value={loading || sdkRate == null ? '—' : pct(sdkRate)}
                tone={sdkRate != null && sdkRate > 0.05 ? 'var(--pf-bad)' : undefined}
                sub={loading ? undefined
                  : sdkRate == null ? 'no calls to measure'
                  : sdkFailed === 0 ? 'no failures'
                  : `${num(sdk.client_error)} client · ${num(sdk.server_error)} server`} />
        <PfTile label="Browser SDK · Avg. Latency" icon={BriefcaseIcon}
                value={loading || sdk?.avg_latency_ms == null ? '—' : ms(sdk.avg_latency_ms)}
                sub="event and session ingest" />
      </>)}
      </div>

      {/* ── traffic ─────────────────────────────────────────────────────────
          [2026-09-11] CHANGED — the Traffic card is off on the landing page. Asked for by Rujal.

          OLD:
            <PfCard title="Traffic" subtitle={`Requests per day over the last ${days} days`}
                    actions={<RangeSelect value={days} onChange={setDays} />}>
              {loading ? <PfSkeleton height={240} />
                       : <TrafficChart data={data?.requests_series || []} colors={BRAND_CHARTS} />}
            </PfCard>

          WHY IT IS DEFENSIBLE TO DROP. This counted HTTP requests to the API — a number that
          answers "is our integration wired up", which is an engineer's question asked once during
          integration and never again. Everyone else opening this page is asking about candidates
          and credits, and the Sessions chart directly below was already drawing that. Two
          area charts stacked, one of which nobody reads, is how a landing page stops being read.

          ⚠️ THE RANGE CONTROL WAS ON THIS CARD AND IS THE ONLY ONE ON THE PAGE. PlatformApp.jsx
          puts 'overview' in RANGELESS_VIEWS *because* this card carried it (see the comment at
          PlatformApp.jsx:116-124), so the page head deliberately renders none. `days` also drives
          the Sessions chart and, when they are switched back on, the per-service tile labels — so
          deleting this card ALONE would have frozen the whole screen at 30 days with nothing left
          to change it. RangeSelect has therefore MOVED to the Sessions card below: same component,
          same `days` state, same position on screen now that Sessions is the topmost panel. That
          is why this removal is not a one-line deletion.

          NOTHING IS DELETED. TrafficChart is untouched in charts.jsx and is still rendered by
          PlatformAnalytics.jsx (account menu → Usage & analytics) and by PlatformOverview.jsx. The
          API field it reads, `requests_series`, is untouched. Restoring this is uncommenting the
          block above and taking `actions` back off the Sessions card. */}

      {/* ── proctoring volume ──────────────────────────────────────────────
          Directly under Traffic, as in the Figma, and for the reason PlatformAnalytics records:
          read together the two answer "is the integration alive" and "are candidates using it",
          and the gap between them is the first symptom of a broken embed. */}
      {/* [2026-09-07] CHANGED — "Proctoring Sessions" -> "Sessions".
          OLD: <PfCard title="Proctoring Sessions" …>

          The customer buys ONE thing. A sitting uses the browser SDK and the proctoring engine
          together, so naming the chart after one half invites the question "then where is the other
          one?" — and any answer to that is a conversation about our internals, which is not what
          this screen is for.

          ONE SERIES, NOT TWO. A second line split by product would imply the two numbers are worth
          comparing. They are not: they are two views of the same sitting, and a customer reconciling
          them against an invoice would be reconciling against something we do not bill that way.

          The headline "Sessions" tile counts the same thing, so the number above and the shape below
          now agree by name as well as by value. */}
      {/* [2026-09-11] ADDED — `actions`, carrying the range control down from the Traffic card that
          was switched off above. Sessions is now the topmost panel on the page, so the control sits
          in the same place on screen and keeps driving the same `days` value it always did. */}
      <PfCard title="Sessions"
              subtitle={`Candidate sessions started per day over the last ${days} days`}
              actions={<RangeSelect value={days} onChange={setDays} />}>
        {loading ? <PfSkeleton height={240} />
                 : <CandidateSessionsChart data={data?.sessions_series || []}
                                           colors={{ ...BRAND_CHARTS, series1: NAVY }} />}
      </PfCard>

      {/* ── latency ────────────────────────────────────────────────────────── */}
      {/* [2026-09-07] TAKEN OFF — latency trend, latency distribution, hour of day and
          weekly pattern. All four remain on Usage & Analytics. See the note above
          for why this is `false &&` and not a comment. */}
      {false && (<>
      <div className="pf-grid two">
        <PfCard title="Latency Trend" subtitle="Average and p95 per day">
          {loading ? <PfSkeleton height={220} />
                   : <LatencyTrend data={data?.latency_series || []} variant="bars"
                                   colors={{ ...BRAND_CHARTS, series1: NAVY, series2: '#12b6bc' }} />}
        </PfCard>
        <PfCard title="Latency Distribution"
                subtitle={`${num(lat.sample_count || 0)} calls, bucketed`}>
          {loading ? <PfSkeleton height={220} />
                   : <LatencyHistogram distribution={lat.distribution || []} ramp
                                       colors={BRAND_CHARTS} />}
        </PfCard>
      </div>

      {/* ── when the traffic happens ───────────────────────────────────────── */}
      <div className="pf-grid two">
        <PfCard title="Hour Of Day" subtitle="When your integration calls us (UTC)">
          {loading ? <PfSkeleton height={200} />
                   : <HourOfDayChart data={data?.hour_of_day || []}
                                     colors={{ ...BRAND_CHARTS, series1: NAVY, series2: '#12b6bc' }} />}
        </PfCard>
        <PfCard title="Weekly Pattern" subtitle="Weekday × hour, UTC">
          {loading ? <PfSkeleton height={200} />
                   : <Heatmap data={data?.heatmap || []} colors={BRAND_CHARTS} />}
        </PfCard>
      </div>

      {/* ── per-endpoint table ─────────────────────────────────────────────── */}
      </>)}

      {/* [2026-09-07] TAKEN OFF — the By Endpoint table. Still on Usage & Analytics. */}
      {false && (
      <PfCard title="By Endpoint"
              subtitle="Volume, failures and latency per API endpoint · click a row to see its calls">
        {loading ? <PfSkeleton height={220} /> : (data?.endpoints || []).length === 0 ? (
          <PfEmpty title="No API calls in this window"
                   hint="Once your backend starts calling the API, every endpoint appears here." />
        ) : (
          <div className="pf-table-wrap">
            <table className="pf-table">
              <thead>
                <tr>
                  <th>Endpoint</th>
                  <th className="num">Calls</th>
                  <th className="num">Success</th>
                  <th className="num">Client err</th>
                  <th className="num">Server err</th>
                  <th className="num">Error rate</th>
                  <th className="num">Avg latency</th>
                </tr>
              </thead>
              <tbody>
                {data.endpoints.map((row) => (
                  // Keyboard-reachable, and DELIBERATELY NOT role="button" — see the long note at
                  // the equivalent row in DataTable.jsx. An explicit role would take the row out of
                  // the table structure for a screen reader AND make every row a substring match
                  // for the e2e suites' getByRole('button', …) locators.
                  <tr key={row.endpoint}
                      className={onDrillEndpoint ? 'clickable' : undefined}
                      tabIndex={onDrillEndpoint ? 0 : undefined}
                      onClick={onDrillEndpoint ? () => onDrillEndpoint(row.endpoint) : undefined}
                      onKeyDown={onDrillEndpoint ? (e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return
                        e.preventDefault()
                        onDrillEndpoint(row.endpoint)
                      } : undefined}>
                    <td className="pf-mono">{row.endpoint}</td>
                    <td className="num">{num(row.total)}</td>
                    <td className="num"><PfPill tone="good">{num(row.success)}</PfPill></td>
                    <td className="num">
                      <PfPill tone={row.client_error ? 'bad' : 'neutral'}>{num(row.client_error)}</PfPill>
                    </td>
                    <td className="num">
                      <PfPill tone={row.server_error ? 'warn' : 'neutral'}>{num(row.server_error)}</PfPill>
                    </td>
                    <td className="num"
                        style={{ color: row.error_rate > 0.05 ? 'var(--pf-bad)' : undefined,
                                 fontWeight: row.error_rate > 0.05 ? 600 : undefined }}>
                      {pct(row.error_rate || 0)}
                    </td>
                    <td className="num">
                      {row.avg_latency_ms == null ? '—' : ms(row.avg_latency_ms)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PfCard>
      )}

      {/* ── what candidates actually did ───────────────────────────────────── */}
      <PfCard title="Candidate Events By Type"
              subtitle="The proctoring signal — what was actually detected">
        {loading ? <PfSkeleton height={200} /> : byType.length === 0 ? (
          <PfEmpty title="No events yet"
                   hint="Events appear once candidates run a proctored exam." />
        ) : (
          <div className="pf-bars">
            {byType.map(([type, n]) => (
              <div className="pf-bar-row" key={type}>
                <div>
                  <div className="pf-bar-label">{type.replace(/_/g, ' ')}</div>
                  {/* Scaled against the LARGEST category, not the total. Against the total, a
                      well-spread breakdown renders as ten near-invisible stubs and the shape — the
                      whole reason for drawing bars — is lost. The count beside it is the exact
                      value either way. */}
                  <div className="pf-bar-track">
                    <div className="pf-bar-fill"
                         style={{ width: `${eventMax ? Math.max((n / eventMax) * 100, 2) : 0}%` }} />
                  </div>
                </div>
                <div className="pf-bar-count">{num(n)}</div>
              </div>
            ))}
          </div>
        )}
      </PfCard>

      {/* ── quick actions ──────────────────────────────────────────────────── */}
      <PfCard title="Quick Actions" subtitle="Common tasks and shortcuts">
        <div className="pf-quick">
          <button className="pf-quick-btn" onClick={onCreateKey}>
            <BriefcaseIcon />Create API keys
          </button>
          <button className="pf-quick-btn" onClick={() => setView('reports')}>
            <ReportIcon />Proctoring Reports
          </button>
          <button className="pf-quick-btn" onClick={() => setView('requests')}>
            <MessageIcon />API Requests
          </button>
          {/* [2026-09-25] CHANGED — Add Subuser now opens the InCruiter account app's Roles & Sub
              Users page (/roles-subuser-list), not this dashboard's own Add Users screen. Asked for
              by Rujal. It is the SAME destination the header's "Add Users" item has used since
              2026-09-08 (PlatformShell.jsx NAV, `accountPath: '/roles-subuser-list'`), so the two
              ways in now agree. accountHref derives the host from this app's own hostname, so dev
              opens dev.v1.account.incruiter.com, test opens test.v1…, prod opens account…
              (productLinks.js). Same tab, exactly as the header item does.
              OLD:
          <button className="pf-quick-btn" onClick={() => setView('members')}>
            <UsersIcon />Add Subuser
          </button> */}
          {/* [2026-09-27] CHANGED — new tab, like IncBot's "Add Users" and this app's own top-bar
              "Add Users" (reason at PlatformShell.jsx, the navItems link).
              OLD: <a className="pf-quick-btn" href={accountHref('/roles-subuser-list')}> */}
          <a className="pf-quick-btn" href={accountHref('/roles-subuser-list')}
             target="_blank" rel="noopener noreferrer">
            <UsersIcon />Add Subuser
          </a>
        </div>
      </PfCard>
    </div>
  )
}

// [2026-09-06] ADDED — video processing's four headline numbers, on one line.
//
// WHY THIS SERVICE GETS ITS OWN TABLE AND THE SDK DOES NOT
// --------------------------------------------------------
// They are not symmetrical products, and giving them symmetrical treatment on this page would be
// misleading. Video processing charges a credit per analysed interview and does real upstream work
// — a download, frame extraction, a 600-second analysis budget — every step of which can fail. The
// browser SDK's per-event telemetry is FREE (app/modules/browser/route.py: `credits_milli` stays 0
// on purpose, so a bill never depends on how much a candidate alt-tabbed) and is a local write that
// essentially cannot fail. One of those needs watching per-call; the other does not.
//
// The Credits screen still shows BOTH side by side, because there the question is "where did my
// balance go" and both surfaces spend. Here the question is "is the expensive thing healthy".
//
// WHERE THE FOUR NUMBERS COME FROM, AND THE ONE THAT IS NOT WHAT IT LOOKS LIKE
// ----------------------------------------------------------------------------
//   Credits utilised — `credits.api_used`, the LEDGER's per-surface counter (SURFACE_API in
//                      app/core/credits.py). Exact, and LIFETIME — there is no dated per-surface
//                      breakdown in the summary, so it is labelled as such rather than being
//                      silently presented alongside three windowed figures as if it matched them.
//   API calls        — summed from the usage rollup over the selected window.
//   Error rate       — recomputed from summed counts, NOT averaged from per-endpoint rates.
//   Avg latency      — call-weighted across the service's endpoints, for the same reason.
//
// ⚠️ THE WINDOWS DIFFER AND THE TABLE SAYS SO IN EVERY CELL'S SUB-LINE. Without that, a reader
// divides lifetime credits by 30-day calls and gets a credits-per-call figure that means nothing.
//
// A NOTE ON THE ERROR RATE READING 0% ON EXISTING DEPLOYMENTS. Until 2026-09-06 the video proxy
// recorded usage ONLY on the success path, so every failure — a 402 for no credits, a timeout, an
// unreachable engine — was invisible to this rollup and the rate was structurally zero. That is
// fixed in app/core/video_processing_proxy.py (`_meter`), but it applies to NEW traffic only:
// historical days hold no failure rows and cannot be backfilled, because the events were never
// written. A long-lived account will therefore show a rate that climbs from zero as the window
// rolls forward, which is correct rather than a bug.
